"use client";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import Image from "next/image";
import { Trigger } from "@radix-ui/react-dropdown-menu";
import { KeyboardArrowDown } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdownMenu";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import EventLink from "@/components/Link/EventLink/EventLink";
import { getEnv } from "@/env";
import logoutUser from "@/lib/auth/logoutUser";
import { useNavigationMessages } from "@/i18n/hooks/useNavigationMessages";

interface UserMenuProps {
  headerUrls?: {
    logout: string;
    accountSettings: string;
    learnerProfile: string;
    resumeBlock: string;
  };
  userImage?: string;
}

function UserMenuTrigger({ children }: { children: React.ReactNode }) {
  const webElement: ElementViewed["webElement"] = {
    elementType: ElementType.Button,
    htmlId: "logged-in-profile-button",
    location: "nav-bar",
    position: "1",
    name: "logged-in-profile-button",
  };

  const { elementViewed, elementClicked } = useElementEvents({ webElement });
  const { inView, ref } = useInView();

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  const handleClick = () => {
    elementClicked();
  };

  return (
    <Trigger asChild>
      <Button
        variant="ghost"
        className="text-medium"
        ref={ref}
        onClick={handleClick}
      >
        {children}
      </Button>
    </Trigger>
  );
}

const itemClass =
  "not-prose inline-block w-full text-base text-primary py-2 px-4 hover:bg-putty-200";

function UserMenuItem({
  href,
  label,
  onClick,
  isDashboard = false,
}: {
  href?: string;
  label: string;
  onClick?: () => void;
  isDashboard?: boolean;
}) {
  // If onClick is provided, render as button
  if (onClick) {
    return (
      <DropdownMenuItem asChild>
        <button onClick={onClick} className={itemClass}>
          {label}
        </button>
      </DropdownMenuItem>
    );
  }

  // Fallback to ensure href is available for link rendering
  if (!href) {
    console.error("UserMenuItem: Either href or onClick must be provided");
    return null;
  }

  return (
    <DropdownMenuItem asChild>
      {isDashboard ? (
        <EventLink
          href={href}
          text={label}
          html_id="logged-in-profile-button"
          location="nav-bar"
          name="dashboard"
          element_type={ElementType.Button}
          class_name={itemClass}
        />
      ) : (
        <a href={href} className={itemClass}>
          {label}
        </a>
      )}
    </DropdownMenuItem>
  );
}

export default function UserMenu({ headerUrls, userImage }: UserMenuProps) {
  const navMessages = useNavigationMessages();
  const careersUrl = getEnv("NEXT_PUBLIC_CAREERS_BASE_URL");
  const homeUrl = getEnv("NEXT_PUBLIC_HOME_BASE_URL");

  /**
   * Handle logout by resetting Segment user identity before redirecting to LMS logout
   * This clears the ajs_user_id cookie and all user traits from Segment
   */
  const handleLogout = () => {
    logoutUser();
  };
  return (
    <DropdownMenu>
      <UserMenuTrigger>
        <Image
          src={userImage || ""}
          alt={navMessages.accountMenuAlt}
          height={36}
          width={36}
          className="rounded-full mr-2"
        />
        <KeyboardArrowDown svgProps={{ className: "flex-none" }} />
      </UserMenuTrigger>
      <DropdownMenuContent className="w-80" style={{ zIndex: 100 }}>
        {headerUrls?.resumeBlock && (
          <UserMenuItem
            href={headerUrls.resumeBlock}
            label={navMessages.resumeLastCourse}
          />
        )}
        <UserMenuItem
          href={homeUrl}
          label={navMessages.userDashboard}
          isDashboard
        />
        <UserMenuItem href={careersUrl} label={navMessages.career} />
        {headerUrls?.learnerProfile && (
          <UserMenuItem
            href={headerUrls.learnerProfile}
            label={navMessages.profile}
          />
        )}
        {headerUrls?.accountSettings && (
          <UserMenuItem
            href={headerUrls.accountSettings}
            label={navMessages.account}
          />
        )}
        <UserMenuItem onClick={handleLogout} label={navMessages.signOut} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
